import GlobalNotificationProvider from '@peachjar/ui/dist/lib/contexts/Notifications/GlobalNotificationProvider';
import * as React from 'react';
import JssProvider from 'react-jss/lib/JssProvider';
import LoadingSpinner from '../components/LoadingSpinner';
import App, { ViewerQueryVariables } from './App';
import AppDrawerProvider from './store/AppDrawer/AppDrawerProvider';
import DealsProvider from './store/Deals/DealsProvider';

import { createGenerateClassName } from '@material-ui/core/styles';
import { components } from '@peachjar/components';
import { createBrowserHistory } from 'history';
import { ApolloProvider, Query } from 'react-apollo';
import { Provider } from 'react-redux';
import { Router } from 'react-router-dom';

import gql from 'graphql-tag';
import idx from 'idx';
import { pageVisibilityApi } from '../helpers/pageVisibilityUtils';
import portalBFFClient from './apollo/portalBFF.apolloClient';
import AppErrorBoundary from './AppErrorBoundary';
import store from './store';

const { visibilityChange } = pageVisibilityApi();
const history = createBrowserHistory();

const { ModalProvider } = components;

const generateClassName = createGenerateClassName({
  productionPrefix: 'jss',
  seed: 'portal',
});

export const VIEWER_QUERY = gql`
  query ViewerQuery {
    sodUser: profile {
      id
      userId
      firstName
      lastName
      email
      loginType
      hierarchy {
        id
        type
      }
      scopes
      roles {
        roles
        forEntity {
          entityId
          entityType
        }
      }
      organization {
        id
        orgCategory
        name
        address {
          address
          googlePlaceId
        }
        type
        orgId
        phone
        url
        email
        taxId
        lod
        originalFilename
      }
      userId
      permission {
        approverLevel
        adminLevel
        uploaderLevel
        staffLevel
        volunteerLevel
        __typename
      }
    }
  }
`;

export type UpdateViewerQueryVariables = (updates: any) => void;

// todo: decouple query variables and updateQueryVariable methods to enable decoupled querying
const initialQueryVariables: ViewerQueryVariables = {
  MyApprovals_sodUser_limit: 10,
  MyApprovals_sodUser_offset: 0,
  MyApprovals_sodUser_filter: null,
  AllApprovals_sodUser_limit: 10,
  AllApprovals_sodUser_offset: 0,
  AllApprovals_sodUser_filter: null,
  PendingApprovals_sodUser_sod: null,
  PendingApprovals_sodUser_sodId: null,
  Reporting_sodUser_limit: 15,
  Reporting_sodUser_offset: 0,
  Reporting_sodUser_filter: null,
};

type Props = {};

type State = {
  queryVariables: ViewerQueryVariables;
  useUploaderQuery: boolean;
  userId?: number | null;
};

class AppContainer extends React.Component<Props, State> {
  state = {
    queryVariables: initialQueryVariables,
    useUploaderQuery: false,
    userId: null,
  };

  componentDidMount() {
    document.addEventListener(
      visibilityChange,
      this.reloadPageOnSwitchAccount,
      false
    );
  }

  componentWillUnmount() {
    document.removeEventListener(
      visibilityChange,
      this.reloadPageOnSwitchAccount,
      false
    );
  }

  reloadPageOnSwitchAccount = async () => {
    const isSwitched = await this.isSwitchedAccount();
    if (isSwitched) {
      window.location.reload();
    }
  };

  isSwitchedAccount = async () => {
    const variables = this.getVariables();
    const { data } = await portalBFFClient.query({
      query: VIEWER_QUERY,
      variables: variables,
      fetchPolicy: 'no-cache',
    });

    const currentUserId = idx(data, _ => _.sodUser.id) || null;
    const { userId } = this.state;

    return currentUserId !== userId;
  };

  getVariables = () => {
    const { queryVariables } = this.state;
    const variables = Object.entries(queryVariables).reduce(
      (result, [key, val]) =>
        val !== null ? { ...result, [key]: val } : result,
      {}
    );
    return variables;
  };

  updateQueryVariables = (updates: ViewerQueryVariables): void => {
    const { queryVariables } = this.state;
    const newQueryVariables = { ...queryVariables, ...updates };
    this.setState({ queryVariables: newQueryVariables });
  };

  resetQueryVariables = (): void => {
    this.setState({ queryVariables: initialQueryVariables });
  };

  handleUseUploaderQuery = (bool: boolean): void => {
    this.setState({ useUploaderQuery: bool });
  };

  isGqlAuthError = (error: any): boolean =>
    error.message.includes('is not authenticated');

  handleGqlError(error: Error) {
    console.error(`Error: ${error}`);
    return <LoadingSpinner />;
  }

  checkUserIsStaffOrVolunteer = (sodUser: any): boolean => {
    if (!sodUser) return false;
    const scopes = idx(sodUser, _ => _.scopes) || null;
    return (
      scopes && ['volunteer', 'staff'].some(scope => scopes.includes(scope))
    );
  };

  render() {
    // TODO: tech debt maybe filter out nulls if we can't accept on server
    const { useUploaderQuery } = this.state;
    const variables = this.getVariables();
    return (
      <Provider store={store}>
        <Router history={history}>
          <JssProvider generateClassName={generateClassName}>
            <AppErrorBoundary>
              <GlobalNotificationProvider>
                <ApolloProvider client={portalBFFClient}>
                  <DealsProvider>
                    <AppDrawerProvider>
                      <ModalProvider>
                        <Query
                          query={VIEWER_QUERY}
                          variables={variables}
                          displayName="ViewerQuery"
                          fetchPolicy="network-only"
                          errorPolicy="all"
                          partialRefetch
                          onCompleted={data => {
                            const userId = idx(data, _ => _.sodUser.userId);
                            this.setState({ userId });
                          }}
                        >
                          {({
                            data,
                            refetch,
                            networkStatus,
                            loading,
                            error,
                            client,
                          }) => {
                            if (error) {
                              return this.handleGqlError(error);
                            }

                            const apolloHelpers = {
                              loading,
                              error,
                              networkStatus,
                              refetch,
                              client,
                            };

                            const gqlStuff = {
                              apolloHelpers,
                              updateQueryVariables: this.updateQueryVariables,
                              resetQueryVariables: this.resetQueryVariables,
                              currentQueryVariables: variables,
                              handleUseUploaderQuery: this
                                .handleUseUploaderQuery,
                              useUploaderQuery,
                              refetchViewerQuery: refetch,
                            };

                            return (
                              <App
                                sodUser={idx(data, _ => _.sodUser)}
                                gqlStuff={gqlStuff}
                              />
                            );
                          }}
                        </Query>
                      </ModalProvider>
                    </AppDrawerProvider>
                  </DealsProvider>
                </ApolloProvider>
              </GlobalNotificationProvider>
            </AppErrorBoundary>
          </JssProvider>
        </Router>
      </Provider>
    );
  }
}

export default AppContainer;
